import {
  ChangeDetectionStrategy,
  Component,
  input,
  output,
} from '@angular/core';
import { UiTooltipDirective } from '../../directives/tooltip/tooltip.directive';

export interface UiAppSidebarItem {
  value: string;
  label: string;
  iconSrc?: string;
  disabled?: boolean;
  /** Título opcional mostrado antes del elemento cuando el sidebar está expandido. */
  sectionTitle?: string;
}

export type UiAppSidebarVariant = 'system' | 'portal';

/**
 * Organismo: barra lateral de navegación de módulos.
 *
 * Renderiza una lista vertical de ítems de navegación asociados al sistema
 * seleccionado. Pensada para usarse junto a `ui-app-header` con solo
 * el select de sistema y sin el select de módulo en el header.
 *
 * Solo presentación; sin HTTP ni Router inyectado.
 */
@Component({
  selector: 'ui-app-sidebar',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [UiTooltipDirective],
  templateUrl: './app-sidebar.component.html',
  styleUrl: './app-sidebar.component.scss',
  host: {
    class: 'ui-app-sidebar-host',
    '[class.ui-app-sidebar-host--collapsed]': 'collapsed()',
  },
})
export class UiAppSidebarComponent {
  /** Variante visual. `system` conserva el comportamiento existente. */
  readonly variant = input<UiAppSidebarVariant>('system');

  /** Logo mostrado únicamente en la variante `portal`. */
  readonly logoSrc = input<string | null>(null);

  /** Isologo mostrado cuando la variante `portal` está colapsada. */
  readonly collapsedLogoSrc = input<string | null>('/images/isologo_azul.png');

  readonly logoAlt = input('');

  /** Lista de ítems de navegación (módulos del sistema seleccionado) */
  readonly items = input<readonly UiAppSidebarItem[]>([]);

  /** Valor del ítem actualmente seleccionado */
  readonly selectedValue = input<string | null>(null);

  /** Ancho del sidebar; sobreescribible vía CSS custom property --ui-sidebar-w */
  readonly collapsed = input(false);

  /** Etiqueta accesible del elemento `<nav>` */
  readonly ariaLabel = input('Navegación de módulos');

  /** Emite el `value` del ítem seleccionado */
  readonly itemSelect = output<string>();

  protected onItemClick(item: UiAppSidebarItem): void {
    if (item.disabled) return;
    this.itemSelect.emit(item.value);
  }
}
